/**
 * CreateVisitPageNew — spec §3.6 with park chips, compact form.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useParks } from '../hooks/useParks';
import { useTrips } from '../hooks/useTrips';
import { useCreateVisit } from '../hooks/useVisits';
import { SkeletonRow } from '../design-system/primitives/SkeletonRow';
import { ParkSelector } from '../components/shared/ParkSelector';
import { ArrowLeft } from 'lucide-react';

export function CreateVisitPageNew(): React.ReactElement {
  const navigate = useNavigate();
  const { data: parks, isLoading: parksLoading } = useParks();
  const { data: trips } = useTrips();
  const createVisit = useCreateVisit();
  const [parkName, setParkName] = React.useState('');
  const [date, setDate] = React.useState('');
  const [period, setPeriod] = React.useState('MORNING');
  const [tripId, setTripId] = React.useState('');
  const [newTripName, setNewTripName] = React.useState('');
  const [newTripStart, setNewTripStart] = React.useState('');
  const [newTripEnd, setNewTripEnd] = React.useState('');
  const [useNewTrip, setUseNewTrip] = React.useState(false);

  const handleSubmit = () => {
    if (!parkName || !date) return;
    createVisit.mutate({
      parkName, visitDate: date, visitPeriod: period as any,
      tripId: useNewTrip ? undefined : tripId || undefined,
      newTripName: useNewTrip ? newTripName : undefined,
      newTripStartDate: useNewTrip ? newTripStart : undefined,
      newTripEndDate: useNewTrip ? newTripEnd : undefined,
    }, { onSuccess: () => navigate('/') });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Plan a Visit</h1>
      </div>

      {parksLoading && <SkeletonRow count={3} width="100%" />}

      <div className="max-w-lg mx-auto space-y-6">
        {/* Where? — park selection dropdown */}
        <section>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Where?</label>
          <ParkSelector
            parks={parks || []}
            value={parkName}
            onChange={setParkName}
            placeholder="Select a park..."
            className="max-w-lg"
          />
        </section>

        {/* When? */}
        <section>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">When?</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card text-gray-900 dark:text-gray-100" />
        </section>

        {/* Period */}
        <section>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Start Time</label>
          <div className="flex gap-2">
            {(['MORNING', 'MIDDAY', 'EVENING'] as const).map((p) => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`flex-1 py-3 text-sm rounded-lg border transition ${period === p ? 'bg-disney-blue-500 text-white border-disney-blue-500' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'}`}>
                {p.charAt(0) + p.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </section>

        {/* Trip */}
        <section>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Trip</label>
          {useNewTrip ? (
            <div className="space-y-2">
              <input placeholder="Trip name" value={newTripName} onChange={(e) => setNewTripName(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card" />
              <div className="flex gap-2">
                <input type="date" value={newTripStart} onChange={(e) => setNewTripStart(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card" />
                <input type="date" value={newTripEnd} onChange={(e) => setNewTripEnd(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card" />
              </div>
            </div>
          ) : (
            <select value={tripId} onChange={(e) => setTripId(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card">
              <option value="">No trip</option>
              {trips?.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.startDate} — {t.endDate})</option>)}
            </select>
          )}
          <button onClick={() => setUseNewTrip(!useNewTrip)} className="mt-2 text-sm text-disney-blue-500 hover:text-disney-blue-600">
            {useNewTrip ? 'Use existing trip' : '+ Create new trip'}
          </button>
        </section>

        {/* CTA */}
        <button onClick={handleSubmit} disabled={createVisit.isPending || !parkName || !date}
          className="w-full py-3 rounded-xl bg-disney-blue-500 text-white font-semibold hover:bg-disney-blue-600 disabled:opacity-50">
          {createVisit.isPending ? 'Creating...' : 'Create Visit →'}
        </button>
      </div>
    </div>
  );
}
